import * as React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat';

import { alphabeticalName, } from '../../../../../../modules/sortingMethods'
import { ClassroomInterface, SkillCategoryInterface, SkillPerformanceData, SkillPracticeRecommendation } from '../interfaces';
import {
  Spinner,
  EXTRA_SMALL_ICON_BASE_SRC,
  Tooltip,
  pluralize,
  titleCaseMultiWordString,
  ActionMenu,
} from '../../../../../Shared/index';
import { NEEDS_MORE_PRACTICE, MEETING_GOAL, APPROACHING_GOAL, NEEDS_SUPPORT, getSmartGroup, smartGroupToClassName, formatMomentumScore, getSortableSmartGroup, ORDERED_SMART_GROUPS, formatLastPracticed, } from '../../shared/shared';

const helpIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/help.svg`;

interface BySkillProps {
  classroom: ClassroomInterface;
  momentumScoreGoal: number;
  data: SkillPerformanceData[] | null;
  onSkillCategoryClick: (skillCategoryId: number) => void;
  handleAssignTargetedPracticeClick: (skillId: number, studentIds: number[]) => void;
  selectedSkillCategory?: SkillCategoryInterface;
  skillPracticeRecommendations: SkillPracticeRecommendation[];
  studentId?: number | string;
}

interface SkillCardProps {
  skill: SkillPerformanceData;
  momentumScoreGoal: number;
  smartGroup: string;
  onSkillCategoryClick: (skillCategoryId: number) => void;
  selectedSkillCategory: SkillCategoryInterface;
  onClickAssignPractice: (skillId: number) => void;
  hasPracticeToAssign: boolean;
  className?: string;
  studentId?: number | string;
}

const SkillCard: React.FC<SkillCardProps> = ({
  skill,
  momentumScoreGoal,
  smartGroup,
  onSkillCategoryClick,
  studentId,
  onClickAssignPractice,
  selectedSkillCategory,
  hasPracticeToAssign,
}) => {
  const { name, avg_exposure, avg_ema, number_of_students, last_practiced, id, avg_questions_answered, } = skill
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const score = Math.round(avg_ema);
  const scoreClassName = smartGroup === NEEDS_MORE_PRACTICE ? smartGroupToClassName[getSmartGroup(avg_exposure, avg_ema, avg_questions_answered, momentumScoreGoal, true)] : smartGroupToClassName[smartGroup];

  function handleAssignPractice() { onClickAssignPractice(id) }

  function viewSkillSubCategories() { onSkillCategoryClick(id) }

  function toggleMenu() { setIsMenuOpen(!isMenuOpen) }

  const actions = [
    !selectedSkillCategory && {
      name: 'View skill sub-categories',
      action: viewSkillSubCategories
    },
    hasPracticeToAssign && {
      name: studentId ? 'Assign targeted practice to student' : 'Assign targeted practice to class',
      action: handleAssignPractice
    }
  ].filter(Boolean);

  return (
    <section className='smart-group-card'>
      <div className="name-and-last-practiced">
        <div className="skill-header">
          <h3>
            {name}
          </h3>
          {actions.length > 0 && (
            <div className="actions-menu-container">
              <ActionMenu
                actions={actions}
                buttonAriaLabel="Open actions menu"
                id={skill.id}
                isOpen={isMenuOpen}
                onToggle={toggleMenu}
              />
            </div>
          )}
        </div>
        <p className="skill-practice-info">
          {studentId ? `Last practiced on ${formatLastPracticed(last_practiced)}` : `${number_of_students} ${pluralize(number_of_students, 'student', 'students')} practiced`}
        </p>
      </div>
      <div className="smart-group-metrics">
        <div className="metric">
          <h4 className="metric-label">{studentId ? '' : 'Avg. '}Momentum Score</h4>
          <span className={`metric-value score ${scoreClassName}`}>
            {formatMomentumScore(score, avg_exposure)}
          </span>
        </div>
        <div className="metric">
          <h4 className="metric-label">{studentId ? '' : 'Avg. '}Exposure</h4>
          <span className={`metric-value exposure ${avg_exposure.toLowerCase()}`}>
            {avg_exposure}
          </span>
        </div>
      </div>
    </section>
  );
};

interface SmartGroupSectionProps {
  skills: SkillPerformanceData[];
  totalSkills: number;
  classPercentage: number;
  momentumScoreGoal: number;
  smartGroup: string;
  onSkillCategoryClick: (skillCategoryId: number) => void;
  selectedSkillCategory: SkillCategoryInterface;
  skillPracticeRecommendations: SkillPracticeRecommendation[];
  studentId?: number | string;
  onClickAssignPractice: (skillId: number) => void;
}

const SmartGroupSection: React.FC<SmartGroupSectionProps> = ({
  skills,
  totalSkills,
  momentumScoreGoal,
  onSkillCategoryClick,
  selectedSkillCategory,
  skillPracticeRecommendations,
  smartGroup,
  studentId,
  onClickAssignPractice,
}) => {
  // Sort skills by Momentum Score (highest to lowest) then by name
  const sortedSkills = React.useMemo(() => {
    return [...skills].sort((a, b) => {
      const aSort = getSortableSmartGroup(smartGroup, a.avg_ema, alphabeticalName(a.name));
      const bSort = getSortableSmartGroup(smartGroup, b.avg_ema, alphabeticalName(b.name));
      return aSort.localeCompare(bSort);
    });
  }, [skills, smartGroup]);

  const getTooltipText = (goal: number) => {
    switch (smartGroup) {
      case MEETING_GOAL:
        return `Skills whose average Momentum Score meets or exceeds your goal of ${goal} and have medium or high average exposure. You can change this goal at any time by clicking the 'Change goal' button.`;
      case APPROACHING_GOAL:
        return `Skills with an average Momentum Score between ${goal - 10} and ${goal - 1} and medium or high average exposure. Students are making steady progress on these skills.`;
      case NEEDS_SUPPORT:
        return `Skills with an average Momentum Score below ${goal - 10} despite medium or high average exposure. These skills may require targeted intervention.`;
      case NEEDS_MORE_PRACTICE:
        return `Skills with low average exposure (5 or fewer questions answered on average), regardless of score. Their average Momentum Score is not yet reliable.`;
      default:
        return '';
    }
  };

  const tooltipText = getTooltipText(momentumScoreGoal);

  return (
    <section className='smart-group-section'>
      <div className="smart-group-header">
        <div className="smart-group-title">
          <h2>{titleCaseMultiWordString(smartGroup)}</h2>
          <Tooltip
            tooltipText={tooltipText}
            tooltipTriggerText={<img alt="Help icon" src={helpIconSrc} />}
          />
        </div>
        <div className="smart-group-stats">
          <span>{totalSkills} {selectedSkillCategory ? pluralize(totalSkills, 'skill sub-category', 'skill sub-categories') : pluralize(totalSkills, 'skill category', 'skill categories')}</span>
        </div>
      </div>
      <div className="smart-group-cards-grid">
        {sortedSkills.map((skill) => (
          <SkillCard
            hasPracticeToAssign={skillPracticeRecommendations.some(rec => rec.concept_id === skill.id)}
            key={skill.id}
            momentumScoreGoal={momentumScoreGoal}
            onClickAssignPractice={onClickAssignPractice}
            onSkillCategoryClick={onSkillCategoryClick}
            selectedSkillCategory={selectedSkillCategory}
            skill={skill}
            smartGroup={smartGroup}
            studentId={studentId}
          />
        ))}
      </div>
    </section>
  );
};

const BySkill: React.FC<BySkillProps> = ({
  classroom,
  data,
  momentumScoreGoal,
  onSkillCategoryClick,
  selectedSkillCategory,
  skillPracticeRecommendations,
  studentId,
  handleAssignTargetedPracticeClick,
}) => {
  const navigate = useNavigate();

  if (!data) { return <Spinner /> }

  // Group skills by smart group - only those that have been practiced
  const skillsWithPractice = data.filter(skill => skill.avg_questions_answered > 0);
  const skillsByGroup = skillsWithPractice.reduce((groups, skill) => {
    const smartGroup = getSmartGroup(skill.avg_exposure, skill.avg_ema, skill.avg_questions_answered, momentumScoreGoal);
    if (!groups[smartGroup]) {
      groups[smartGroup] = [];
    }
    groups[smartGroup].push(skill);
    return groups;
  }, {} as Record<string, SkillPerformanceData[]>);

  const totalSkillsAvailable = data.length;
  
  const getClassPercentage = (count: number) => Math.round((count / totalSkillsAvailable) * 100);
  
  const skillsNotPracticed = data.filter(skill => skill.avg_questions_answered === 0);
  const hasUnpracticedSkills = skillsNotPracticed.length > 0;
  const allSkillsUnpracticed = skillsNotPracticed.length === data.length

  const handleViewAllSkills = () => {
    navigate(`/teachers/progress_reports/skills_report/classroom/${classroom.id}/performance_breakdown/by_skill`);
  };

  const onClickAssignPractice = (skillId: number) => {
    const studentIdsForAssignment = studentId ? [Number(studentId)] : classroom.students.map(s => s.id)
    handleAssignTargetedPracticeClick(skillId, studentIdsForAssignment)
  }

  const sharedProps = {
    momentumScoreGoal,
    onSkillCategoryClick,
    selectedSkillCategory,
    skillPracticeRecommendations,
    studentId,
  }

  if (allSkillsUnpracticed) {
    return (
      <div className="by-skill">
        <div className="empty-state">
          <h2>
            No {selectedSkillCategory ? 'skill sub-categories' : 'skill categories'} have been practiced yet
          </h2>
          <button 
            className="quill-button contained medium green focus-on-light"
            onClick={handleViewAllSkills}
            type="button"
          >
            View all skills in Performance Breakdown
          </button>
        </div>
      </div>
    )
  }

  const smartGroupSections = ORDERED_SMART_GROUPS.map(smartGroup => {
    if (!(skillsByGroup[smartGroup]?.length > 0)) { return }

    return (
      <SmartGroupSection
        {...sharedProps}
        classPercentage={getClassPercentage(skillsByGroup[smartGroup].length)}
        key={smartGroup}
        onClickAssignPractice={onClickAssignPractice}
        skills={skillsByGroup[smartGroup]}
        smartGroup={smartGroup}
        totalSkills={skillsByGroup[smartGroup].length}
      />
    )
  }).filter(Boolean)

  return (
    <div className="by-skill">
      {smartGroupSections}
      {hasUnpracticedSkills && (
        <section className="unpracticed-section-wrapper">
          <div className="unpracticed-section">
            <h2>
              {skillsNotPracticed.length} {selectedSkillCategory ? pluralize(skillsNotPracticed.length, 'skill sub-category', 'skill sub-categories') : pluralize(skillsNotPracticed.length, 'skill category', 'skill categories')} {pluralize(skillsNotPracticed.length, "hasn't", "haven't")} been practiced yet
            </h2>
            <button 
              className="quill-button outlined medium grey focus-on-light"
              onClick={handleViewAllSkills}
              type="button"
            >
              View all skills in Performance Breakdown
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default BySkill
